// Importing package.json at runtime to avoid TypeScript module resolution errors
const getContainer = async (remote, timeout = 2000) => {
  function check(resolve, reject, time = 0) {
    if (window[remote]) {
      return resolve(window[remote]);
    }
    if (time > timeout) {
      return reject(`Poll timed out while looking for '${remote}' container`);
    }
    setTimeout(check, 100, resolve, reject, time + 100);
  }
  return new Promise<{ get: Function }>(check);
};

function defaultExtract<T>(
  remoteModule: T,
  remoteName: string,
  module: string
) {
  if (!self[remoteName]) {
    self[remoteName] = {};
  }

  self[remoteName][module] = remoteModule;
}

export const federate = async <T>(
  remote: string,
  mod: string,
  extractCb?: (remote: T) => void,
  onError: (err: Error) => void = console.error
): Promise<T> => {
  if (typeof window !== "undefined") {
    return getContainer(remote)
      .then((container) => container.get(mod))
      .then((containerModule) => containerModule())
      .then((remoteModule) => {
        void (extractCb
          ? extractCb(remoteModule)
          : defaultExtract(remoteModule, remote, mod));
        return remoteModule;
      })
      .catch((err) => {
        void onError?.(err);
      });
  }
};

export const getMajorVersionError = (pckgName: string) =>
  `[Federated Version Validation]: Federated [${pckgName}] must match major of mwa-analytics`;

export const getMinorVersionError = (pckgName: string) =>
  `[Federated Version Validation]: Federated [${pckgName}] must match minor of other packages`;

/**
 * The purpose of Federated Config Version Checker module here is to verify that version of the
 * federated modules included match that of the mwa-analytics version that was installed. For an
 * a diagram of the relationship please see the following:
 * imgs/Modular Analytics design board - LAM Semvar Implementation.jpg
 *
 * @returns {ReturnType<typeof federatedConfigVersionChecker>}
 */
export function federatedConfigVersionChecker() {
  let majorToMatch;
  return {
    majorCheck: function (pckgName: string, pckgVersion: string) {
      if (majorToMatch === undefined) {
        majorToMatch = pckgVersion.split(".")[0];
      }
      const [pckgMajor, pckgMinor] = pckgVersion.split(".");

      if (majorToMatch !== pckgMajor) {
        throw new Error(getMajorVersionError(pckgName));
      }

      this.versions.push([pckgName, pckgMinor]);
    },
    minorCheck: function () {
      const minorToMatch = this.versions[0][1];
      this.versions.reduce((valid, currentVersion) => {
        const validMinor = valid && currentVersion[1] === minorToMatch;

        if (validMinor === false) {
          throw new Error(getMinorVersionError(currentVersion[0]));
        }

        return validMinor;
      }, true);
    },
    versions: [] as [string, string][],
  };
}
